import moment from 'moment'
import R from 'ramda'
import numeral from 'numeral'

import { DATE_TIME_FORMAT_UI } from '../enum'

export const ROW_COLOR_INFO_KEY = 'rowColor'

export const getAggregationData = (data, columnHeaders, selectedCalcs) => {
  const result = {
    Sum: {},
    Avg: {},
    Min: {},
    Max: {},
  }
  const effData = data.filter(f => f.VALUE1 !== 'Total')

  for (const item of effData) {
    columnHeaders.forEach(ch => {
      if (ch.id !== 'ROW_NUM') {
        if (ch.dataType === 'NUMBER') {
          const rval = (item[ch.id] || '').replaceAll(',', '')
          if (rval && !isNaN(rval)) {
            const val = parseFloat(rval)
            const sum = result.Sum[ch.id] || 0
            result.Sum[ch.id] = sum + val
            result.Avg[ch.id] = result.Sum[ch.id] / effData.length
            const min = result.Min[ch.id] || val
            result.Min[ch.id] = min < val ? min : val
            const max = result.Max[ch.id] || val
            result.Max[ch.id] = max > val ? min : val
          }
        }
        if (ch.dataType === 'DATE') {
          const date = item[ch.id] && moment(item[ch.id])
          if (date) {
            const min = result.Min[ch.id] || date
            result.Min[ch.id] = min < date ? min : date
            const max = result.Max[ch.id] || date
            result.Max[ch.id] = max > date ? min : date
          }
        }
      }
    })
  }

  const firstStrCol = columnHeaders.find(col => col.dataType === 'STRING')

  return Object.keys(result)
    .filter(calcId => selectedCalcs.includes(calcId))
    .map(calcId => {
      const aggRow = result[calcId]
      return {
        aggRow: true,
        [firstStrCol ? firstStrCol.id : 'ROW_NUM']:
          calcId === 'Sum' ? 'Net' : calcId,
        ...aggRow,
      }
    })
}

const compareItems = (item1, item2, dataType) => {
  if (item1 == null && item2 == null) return 0

  if (item1 == null) return -1

  if (item2 == null) return 1

  if (dataType === 'NUMBER') {
    return item1 - item2
  }
  if (dataType === 'DATE') {
    return moment(item1) > moment(item2) ? 1 : -1
  }
  if (item1 === item2) return 0
  return item1 > item2 ? 1 : -1
}

export const multiFieldSort = (item1, item2, fieldArray) => {
  // sortOrderArray.
  for (const column of fieldArray) {
    const firstItem = item1[column.id]
    const secondItem = item2[column.id]
    let result = 0
    if (column.order === 'DESC') {
      result = compareItems(secondItem, firstItem, column.dataType)
    } else {
      result = compareItems(firstItem, secondItem, column.dataType)
    }
    if (result !== 0) return result
  }
  return 0
}

export const columnToGridHeaders = headers => {
  return headers.map(col => {
    const colWidth = col.display.length * 10
    const displayFormat = col.displayFormat
    return {
      display: col.display,
      ident: col.id || col.ident,
      width: col.width || (colWidth > 150 ? colWidth : 150),
      alignment: (col.align && col.align.toLowerCase()) || 'center',
      type: col.type || 'str',
      ellipsis: true,
      dataType:
        col.displayFormat || ''.includes('$')
          ? 'Currency'
          : col.dataType || col.type === 'num'
          ? 'NUMBER'
          : col.type === 'date-time'
          ? 'TIMESTAMP'
          : 'STRING',
      dataFormatter: ({ value }) => {
        if (value == null) return value

        if (
          col.dataType === 'DATE' ||
          col.dataType === 'TIMESTAMP' ||
          col.type === 'date-time'
        ) {
          const dt = moment(value)
          if (dt.isValid())
            return dt.format(displayFormat || DATE_TIME_FORMAT_UI)
          return value
        }

        if (col.dataType === 'NUMBER' || col.type === 'num') {
          return numeral(value).format(displayFormat || '#,#.##')
        }
        return value
      },
    }
  })
}

export const visibleColumns = (profile, columnHeaders) =>
  columnHeaders.filter(
    col =>
      !profile.hiddenColumns ||
      !profile.hiddenColumns.split(',').includes(col.id)
  )

export const toXlsxColumns = columns =>
  columns.map(col => {
    const colWidth = col.display.length * 2
    return {
      header: col.display,
      display: col.display,
      key: col.id || col.ident,
      width: (col.width && col.width / 10) || (colWidth > 10 ? colWidth : 10),
      type: col.type,
      displayFormat: col.excelFormat || col.displayFormat,
    }
  })

export const toXlsxData = (data, columnHeaders) => {
  const gridHeaders = columnToGridHeaders(columnHeaders)

  const columnMap = R.zipObj(
    gridHeaders.map(col => col.ident),
    gridHeaders
  )
  return data.map(row =>
    R.mapObjIndexed((value, key) => {
      if (key === 'row') {
        return value
      }
      const col = columnMap[key]
      if (col == null) return null
      return col.dataFormatter({ value })
    }, row)
  )
}

export const getColorFromColorInfoMap = (row, column, colorInfoMap) => {
  const rowColorInfo = colorInfoMap[row]
  if (rowColorInfo != null) {
    const columnColor = rowColorInfo[column] || rowColorInfo[ROW_COLOR_INFO_KEY]
    if (columnColor != null) {
      return columnColor
    }
  }
  const wholeColumnColorInfo = colorInfoMap[undefined]
  if (wholeColumnColorInfo != null) {
    const color = wholeColumnColorInfo[column]
    if (color != null) {
      return color
    }
  }
}

export const getColorInfoMapFromList = colorInfo => {
  const colorMap = {}
  colorInfo.forEach(item => {
    if (item.column) {
      const columnColorInfo = colorMap[item.row]
      if (columnColorInfo) {
        columnColorInfo[item.column] = item.color
      } else {
        const initialColorInfo = { [item.column]: item.color }
        colorMap[item.row] = initialColorInfo
      }
    } else {
      const rowColorInfo = colorMap[item.row]
      if (!rowColorInfo) {
        colorMap[item.row] = { [ROW_COLOR_INFO_KEY]: item.color }
      }
    }
  })
  return colorMap
}

export const getColumnCountFromMetaInfo = metaInfo => {
  return R.reduce(
    R.max,
    0,
    metaInfo.map(item => item.column)
  )
}

export const getRowCountFromMetaInfo = metaInfo => {
  return R.reduce(
    R.max,
    0,
    metaInfo.map(item => item.row)
  )
}

export const generateGridHeaderForMetaInfo = metaInfo => {
  const columnCount = getColumnCountFromMetaInfo(metaInfo)
  return [...Array(columnCount)].map((_, index) => {
    return {
      ident: index + 1 + '',
      display: index + 1 + '',
      type: 'str',
      ellipsis: true,
      width: 100,
      dataGetter: ({ rowData, header }) =>
        rowData[header.ident] && rowData[header.ident].content,
    }
  })
}

export const generateMetaInfoMap = metaInfo => {
  const newMeta = R.groupBy(item => item.position, metaInfo)
  return R.mapObjIndexed(value => {
    const rowCount = getRowCountFromMetaInfo(value)
    const grouped = R.groupBy(item => item.row, value)
    ;[...new Array(rowCount)].forEach((_, index) => {
      const val = grouped[index + 1]
      if (!val) {
        grouped[index + 1] = []
      }
    })
    return Object.values(
      R.mapObjIndexed(rowData => {
        return R.fromPairs(rowData.map(item => [item.column, item]))
      }, grouped)
    )
  }, newMeta)
}

export const getHeadersFromMap = metaInfoMap => {
  return Object.keys(metaInfoMap).flatMap(headerKey => {
    if (+headerKey <= 0) {
      const rows = metaInfoMap[headerKey]
      rows.push({})
      return rows
    } else {
      return []
    }
  })
}

export const getFootersFromMap = metaInfoMap => {
  return Object.keys(metaInfoMap).flatMap(headerKey => {
    if (+headerKey > 0) {
      const rows = metaInfoMap[headerKey]
      rows.push({})
      return rows
    } else {
      return []
    }
  })
}
